<template>
  <app-page class="ex-page" fill lean>
    <!-- Aktionen wie bei Verkäufen (Kundenwunsch 13.10.2026): weiße Knöpfe mit Icon und Beschriftung; zuerst „Alle Gruppen
         auf-/zuklappen“, immer sichtbar, gesperrt außer beim Monatsabschluss mit Daten -->
    <template v-if="!handy" #actions>
      <app-button
        :icon="expanded ? 'fold' : 'unfold'"
        :disabled="!groupsToggleable"
        @click="toggleGroups"
      >{{ expanded ? 'Alle zuklappen' : 'Alle aufklappen' }}</app-button>
      <app-button icon="refresh" title="Vorschau aktualisieren" @click="refreshPreview">Aktualisieren</app-button>
      <app-button
        icon="download"
        :disabled="exportDisabled"
        :title="exportTitle"
        @click="runExport"
      >{{ exportDef.label }}</app-button>
    </template>

    <!-- Werkzeugleiste wie bei Verkäufen (Kundenwunsch 13.10.2026): links Export-Auswahl, Zeitraum, Suche und Hinweis,
         rechts Gruppierung bzw. Linien (ex-rechts) -->
    <template #toolbar>
      <app-select
        class="ex-export"
        :class="{ 'ex-full': handy }"
        prefix="Export"
        :value="exportId"
        :items="exportItems"
        :width="handy ? null : (touch ? 232 : 208)"
        :block="handy"
        @input="selectExport"
      />
      <period-field
        v-if="isCitybus"
        :class="{ 'ex-full': handy }"
        months-only
        :from-month="fromMonth"
        :month="month"
        :max="currentMonth"
        :block="handy"
        @apply="onCitybusPeriod"
      />
      <app-month-stepper
        v-else
        v-model="month"
        :class="{ 'ex-full': handy }"
        :max="currentMonth"
        :grow="handy"
        label="Berichtsmonat"
      />
      <app-search v-if="!handy" v-model="search" :width="260" placeholder="In der Vorschau suchen" @input="onSearch" />
      <span v-if="exportId === 'rbo'" class="ex-hint" :class="{ 'ex-hint--phone': handy }">Abrechnungszeitraum {{ rboPeriod }}</span>
      <span v-if="monthRunning" class="ex-hint ex-hint--running" :class="{ 'ex-hint--phone': handy }">Monat läuft noch – Daten bis {{ todayText }}</span>
      <app-select
        v-if="exportId === 'monthlyClosing'"
        class="ex-rechts"
        :class="{ 'ex-full': handy }"
        icon="layers"
        hide-prefix
        prefix="Gruppierung"
        :value="maGrouping"
        :items="groupingItems"
        :block="handy"
        @input="setGrouping"
      />
      <app-select
        v-if="exportId === 'nvm'"
        class="ex-rechts"
        :class="{ 'ex-full': handy }"
        prefix="Linien"
        :placeholder="linesLabel"
        :block="handy"
        :disabled="!nvmLines.length"
        @open="onLinesOpen"
        @close="onLinesClose"
      >
        <template #menu>
          <div class="ex-lines" role="group" aria-label="Linien">
            <app-checkbox class="ex-lines__row" :value="draftAll" @input="toggleDraftAll">Alle Linien</app-checkbox>
            <div class="app-menu__divider" role="separator"></div>
            <app-checkbox
              v-for="line in nvmLines"
              :key="line.line_id"
              class="ex-lines__row"
              :value="linesDraft.indexOf(line.line_id) !== -1"
              @input="toggleDraftLine(line.line_id, $event)"
            >{{ lineText(line) }}</app-checkbox>
          </div>
        </template>
      </app-select>
    </template>

    <div class="app-grid-fill ex-preview" :class="{ 'ex-preview--hidden': handy }">
      <monthly-closing-component
        v-if="exportId === 'monthlyClosing'"
        :key="exportId"
        ref="active"
        embedded
        :report-month="month"
        :initial-grouping="maGrouping"
        @hook:mounted="onActiveMounted"
      />
      <mvv-export-component
        v-else-if="exportId === 'mvv'"
        :key="exportId"
        ref="active"
        embedded
        :report-month="month"
        @hook:mounted="onActiveMounted"
      />
      <rbo-export-component
        v-else-if="exportId === 'rbo'"
        :key="exportId"
        ref="active"
        embedded
        :report-month="month"
        @hook:mounted="onActiveMounted"
      />
      <nvm-export-component
        v-else-if="exportId === 'nvm'"
        :key="exportId"
        ref="active"
        embedded
        :report-month="month"
        @hook:mounted="onActiveMounted"
      />
      <landshut-export-component
        v-else-if="exportId === 'landshut'"
        :key="exportId"
        ref="active"
        embedded
        :report-month="month"
        @hook:mounted="onActiveMounted"
      />
      <citybus-flexibus-component
        v-else-if="exportId === 'citybus'"
        :key="exportId"
        ref="active"
        embedded
        :month="month"
        :from-month="fromMonth"
        @hook:mounted="onActiveMounted"
      />
      <app-state-overlay
        v-if="!handy"
        :state="state"
        :title="stateTitle"
        :text="stateText"
        @retry="refreshPreview"
      />
    </div>

    <app-grid-footer v-if="!handy" ref="fuss" :page-sizes="pageSizes" :pager-always="false">
      <template #summary>
        <template v-for="(part, i) in summaryParts">
          <span v-if="i > 0" :key="'s' + i" class="app-sep" aria-hidden="true">·</span>
          <span :key="'p' + i">{{ part.before }}<strong :class="{ 'ex-zero': part.zero }">{{ part.value }}</strong>{{ part.after }}</span>
        </template>
        <template v-if="searchActive && summaryParts.length">
          <span class="app-sep" aria-hidden="true">·</span>
          <span class="ex-searchnote">Suche aktiv: Export enthält nur Treffer</span>
        </template>
      </template>
    </app-grid-footer>

    <div v-else class="ex-phone">
      <p v-if="state === 'loading'" class="ex-phone__line" role="status">{{ stateText }}</p>
      <template v-else-if="state === 'error'">
        <p class="ex-phone__line ex-phone__line--strong" role="alert">{{ stateTitle }}</p>
        <app-button block @click="refreshPreview">Erneut versuchen</app-button>
      </template>
      <p v-else-if="state === 'empty' && !searchActive" class="ex-phone__line" role="status">
        <span class="ex-phone__strong">{{ stateTitle }}</span> {{ stateText }}
      </p>
      <p v-else class="ex-phone__line ex-phone__line--sum">
        <template v-for="(part, i) in summaryParts">
          <span v-if="i > 0" :key="'s' + i" class="app-sep" aria-hidden="true">·</span>
          <span :key="'p' + i">{{ part.before }}<strong :class="{ 'ex-zero': part.zero }">{{ part.value }}</strong>{{ part.after }}</span>
        </template>
      </p>
      <app-button
        kind="primary"
        icon="download"
        block
        :disabled="exportDisabled"
        :title="exportTitle"
        @click="runExport"
      >{{ exportDef.label }}</app-button>
    </div>
  </app-page>
</template>

<script>
// Exporte (Spezifikation 4.3): eine Seite für Monatsabschluss und Verbund-Exporte. Die bestehenden Export-Komponenten
// laufen eingebettet (Prop embedded: ohne eigene Leiste, Filterzeile, DX-Pager, Ladeanzeige und Fehlerzeile); die Seite
// stellt Werkzeugleiste, Zustand (AppStateOverlay), Fußleiste und den einen Export-Knopf. Immer nur die gewählte
// Komponente ist montiert. Laden, Spalten und Dateien kommen unverändert aus den Komponenten.
import moment from 'moment';
import MonthlyClosingComponent from '@/components/MonthlyClosingComponent';
import MvvExportComponent from '@/components/MvvExportComponent';
import RboExportComponent from '@/components/RboExportComponent';
import NvmExportComponent from '@/components/NvmExportComponent';
import LandshutExportComponent from '@/components/LandshutExportComponent';
import CitybusFlexibusComponent from '@/components/CitybusFlexibusComponent';
import PeriodField from '@/components/sales/PeriodField';
import DataGridMixin from '@/mixins/DataGridMixin';
import { formatEuro, formatNumber, formatMonth, formatDate, parseApiDecimal, plural, LEER } from '@/utils/format';

const KEY_EXPORT = 'exporte.auswahl';
const KEY_GROUPING = 'exporte.gruppierung';

// Export → Name, Knopftext und Art (CSV-Methoden verändern Grid-Zeilen, danach wird neu geladen)
const EXPORTS = {
  monthlyClosing: { name: 'Monatsabschluss', label: 'Excel-Export', csv: false },
  mvv: { name: 'MVV', label: 'CSV-Export', csv: true },
  rbo: { name: 'RBO', label: 'CSV-Export', csv: true },
  nvm: { name: 'NVM', label: 'Excel-Export', csv: false },
  landshut: { name: 'Landshut', label: 'CSV-Export', csv: true },
  citybus: { name: 'Citybus & Flexibus', label: 'Excel-Export', csv: false }
};

const EXPORT_ITEMS = [
  { value: 'monthlyClosing', text: 'Monatsabschluss' },
  { divider: true },
  { header: 'Verbund-Exporte' },
  { value: 'mvv', text: 'MVV' },
  { value: 'rbo', text: 'RBO' },
  { value: 'nvm', text: 'NVM' },
  { value: 'landshut', text: 'Landshut' },
  { value: 'citybus', text: 'Citybus & Flexibus' }
];

// Werte wie in MonthlyClosingComponent (groupSelection)
const GROUPING_ITEMS = [
  { value: null, text: 'Tarifstufe' },
  { value: 'price', text: 'Preisstufe' },
  { value: 'line', text: 'Linie' }
];

function readStore(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStore(key, value) {
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch (e) {
    // Speicher nicht verfügbar: Auswahl gilt bis zum Neuladen
  }
}

function toNumber(value) {
  return typeof value === 'number' ? value : parseApiDecimal(value);
}

// Summenwert für die Fußleiste: leer → 0 (grau), sonst nur endliche Zahlen, andernfalls „–“
function part(value, empty, format) {
  const n = empty ? 0 : toNumber(value);
  const ok = typeof n === 'number' && isFinite(n);
  return { value: ok ? format(n) : LEER, zero: ok && n === 0, n: ok ? n : 0 };
}

function isZeroValue(value) {
  if (typeof value === 'number') {
    return value === 0;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return parseApiDecimal(value) === 0;
  }
  return false;
}

function sameSet(a, b) {
  const x = a || [];
  const y = b || [];
  return x.length === y.length && x.every(v => y.indexOf(v) !== -1);
}

export default {
  name: 'Exports',
  components: {
    MonthlyClosingComponent,
    MvvExportComponent,
    RboExportComponent,
    NvmExportComponent,
    LandshutExportComponent,
    CitybusFlexibusComponent,
    PeriodField
  },
  mixins: [DataGridMixin],
  data() {
    const stored = readStore(KEY_EXPORT);
    const grouping = readStore(KEY_GROUPING);
    // Vormonat in Ortszeit, wird nicht gemerkt
    const month = moment().subtract(1, 'month').format('YYYY-MM');
    return {
      exportId: EXPORTS[stored] ? stored : 'monthlyClosing',
      month,
      fromMonth: month,
      maGrouping: grouping === 'price' || grouping === 'line' ? grouping : null,
      search: '',
      expanded: false,
      state: 'loading',
      loadError: false,
      rowCount: 0,
      summaryData: null,
      nvmLines: [],
      nvmSelected: [],
      linesDraft: [],
      currentMonth: moment().format('YYYY-MM'),
      todayText: formatDate(new Date()),
      exportItems: EXPORT_ITEMS,
      groupingItems: GROUPING_ITEMS
    };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    exportDef() {
      return EXPORTS[this.exportId];
    },
    isCitybus() {
      return this.exportId === 'citybus';
    },
    searchActive() {
      return this.search !== '';
    },
    monthRunning() {
      return this.month === this.currentMonth;
    },
    // gleiche Formel wie RboExportComponent (15. des Vormonats bis 14. des gewählten Monats), nur Anzeige
    rboPeriod() {
      const toMonth = Number(this.month.substring(5, 7));
      const toYear = Number(this.month.substring(0, 4));
      const fromMonth = ((((toMonth - 2) % 12) + 12) % 12) + 1;
      const fromYear = fromMonth == 12 ? toYear - 1 : toYear;
      const pad = n => String(n).padStart(2, '0');
      return '15.' + pad(fromMonth) + '.' + (fromYear !== toYear ? fromYear : '') + '–14.' + pad(toMonth) + '.' + toYear;
    },
    periodPhrase() {
      if (this.isCitybus && this.fromMonth && this.fromMonth !== this.month) {
        return 'von ' + formatMonth(this.fromMonth) + ' bis ' + formatMonth(this.month);
      }
      return 'im ' + formatMonth(this.month);
    },
    fileName() {
      const year = this.month.substring(0, 4);
      const mon = this.month.substring(5, 7);
      switch (this.exportId) {
        case 'mvv':
          return 'MVV_Export_' + year + '_' + mon + '.csv';
        case 'rbo':
          return 'rboexport.csv';
        case 'nvm':
          return 'NVM.xlsx';
        case 'landshut':
          return 'Landshut' + this.$store.state.customerId + '_' + this.$store.state.customerName + '_' + year + '_' + mon + '.csv';
        case 'citybus':
          return 'CitybusFlexibusVerkäufe.xlsx';
        default:
          return 'Monatsabschluss.xlsx';
      }
    },
    // Auf-/Zuklappen nur beim gruppierten Monatsabschluss mit Daten
    groupsToggleable() {
      return this.exportId === 'monthlyClosing' && this.state !== 'loading' && this.rowCount > 0;
    },
    exportDisabled() {
      return this.state === 'loading' || this.state === 'error' || this.rowCount === 0;
    },
    exportTitle() {
      if (this.state === 'loading') {
        return 'Vorschau wird geladen …';
      }
      if (this.state === 'error') {
        return 'Vorschau konnte nicht geladen werden.';
      }
      return this.rowCount === 0 ? 'Keine Daten zum Herunterladen' : this.fileName;
    },
    stateTitle() {
      if (this.state === 'empty') {
        return this.searchActive ? 'Keine Treffer' : 'Keine Daten für ' + this.exportDef.name + ' ' + this.periodPhrase;
      }
      return this.state === 'error' ? 'Vorschau konnte nicht geladen werden.' : null;
    },
    stateText() {
      if (this.state === 'loading') {
        return 'Vorschau wird geladen …';
      }
      if (this.state === 'empty') {
        if (this.searchActive) {
          return 'Die Suche „' + this.search + '“ findet in der Vorschau nichts.';
        }
        return this.isCitybus ? 'Wählen Sie oben einen anderen Zeitraum.' : 'Wählen Sie oben einen anderen Monat.';
      }
      return null;
    },
    summaryParts() {
      const d = this.summaryData;
      if (!d || this.state === 'loading' || this.state === 'error') {
        return [];
      }
      const empty = this.rowCount === 0;
      if (this.exportId === 'monthlyClosing') {
        const sum = part(d.sum, empty, formatEuro);
        const sales = part(d.sales, empty, formatNumber);
        const cancel = part(d.cancel, empty, formatNumber);
        const parts = [
          Object.assign({ before: 'Summe ', after: '' }, sum),
          Object.assign({ before: '', after: ' ' + plural(sales.n, 'Verkauf', 'Verkäufe') }, sales),
          Object.assign({ before: 'davon ', after: ' storniert' }, cancel)
        ];
        // Die Variante Preisstufe (option=monthlyClosingByPriceLevel) liefert keine Fahrgastzahlen
        if (this.maGrouping !== 'price') {
          const pax = part(d.pax, empty, formatNumber);
          parts.push(Object.assign({ before: '', after: ' ' + plural(pax.n, 'Fahrgast', 'Fahrgäste') }, pax));
        }
        return parts;
      }
      if (this.exportId === 'mvv') {
        return [
          Object.assign({ before: 'Anzahl ', after: '' }, part(d.count, empty, formatNumber)),
          Object.assign({ before: 'Gesamtbetrag ', after: '' }, part(d.amount, empty, formatEuro))
        ];
      }
      const rows = part(this.rowCount, empty, formatNumber);
      return [Object.assign({ before: '', after: ' ' + plural(rows.n, 'Zeile', 'Zeilen') }, rows)];
    },
    linesLabel() {
      const total = this.nvmLines.length;
      const n = this.nvmSelected.length;
      if (!total || !n || n >= total) {
        return 'alle';
      }
      return n + ' von ' + total;
    },
    draftAll() {
      return this.nvmLines.length > 0 && this.linesDraft.length === this.nvmLines.length;
    }
  },
  watch: {
    month(value) {
      // Citybus & Flexibus beginnt mit dem zuletzt gewählten Monat (von = bis)
      if (!this.isCitybus) {
        this.fromMonth = value;
      }
    },
    touch() {
      this.$nextTick(this.updateDimensions);
    },
    handy() {
      this.$nextTick(this.updateDimensions);
    }
  },
  created() {
    // DX-Instanz nicht reaktiv halten
    this.grid = null;
  },
  beforeDestroy() {
    this.grid = null;
  },
  methods: {
    // Nach dem Mount der gewählten Komponente: Ereignisse ihres Grids für Zustand, Fußleiste und Darstellung
    onActiveMounted() {
      const active = this.$refs.active;
      const holder = active && active.$refs[active.gridRefName];
      const grid = holder && holder.instance;
      if (!grid) {
        return;
      }
      this.grid = grid;
      this.loadError = false;
      this.state = 'loading';
      this.rowCount = 0;
      this.summaryData = null;
      grid.on('contentReady', (e) => this.onGridContentReady(grid, e));
      grid.on('dataErrorOccurred', () => {
        if (this.grid === grid) {
          this.loadError = true;
          this.state = 'error';
        }
      });
      grid.on('cellPrepared', (e) => this.onGridCellPrepared(e));
      // Ladezustand am Data-Controller statt an grid.getDataSource(): DevExtreme legt die DataSource bei jeder
      // Änderung von grouping.* neu an (Aufklappen beim Monatsabschluss, Monatsschritt bei RBO), ein Abo an der
      // DataSource ginge dabei verloren. Der Controller lebt so lange wie das Grid (private API wie
      // getController('export') in den Komponenten); ein Abmelden ist nicht nötig.
      const dataController = typeof grid.getController === 'function' ? grid.getController('data') : null;
      const onLoading = (isLoading) => {
        if (this.grid !== grid || !isLoading) {
          return;
        }
        // Laden aller Zeilen für einen Export (loadAll, Excel und CSV) ist kein Neuladen der Vorschau: danach kommt
        // kein contentReady, der Zustand bliebe sonst auf „wird geladen“ stehen
        const adapter = dataController && typeof dataController.dataSource === 'function' ? dataController.dataSource() : null;
        if (adapter && typeof adapter.isCustomLoading === 'function' && adapter.isCustomLoading()) {
          return;
        }
        this.loadError = false;
        this.state = 'loading';
      };
      if (dataController && dataController.loadingChanged) {
        dataController.loadingChanged.add(onLoading);
      } else if (grid.getDataSource()) {
        grid.getDataSource().on('loadingChanged', onLoading);
      }
      active.$watch('expanded', (value) => {
        this.expanded = !!value;
      }, { immediate: true });
      if (this.exportId === 'nvm') {
        active.$watch('lines', (value) => {
          this.nvmLines = Object.freeze((value || []).slice());
        }, { immediate: true });
        active.$watch('selectedLineIds', (value) => {
          this.nvmSelected = (value || []).slice();
        }, { immediate: true });
      }
    },
    onGridContentReady(grid) {
      if (this.grid !== grid) {
        return;
      }
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      const dataSource = grid.getDataSource();
      if (dataSource && dataSource.isLoading()) {
        this.state = 'loading';
        return;
      }
      if (this.loadError) {
        this.state = 'error';
        return;
      }
      const total = Number(grid.totalCount());
      this.rowCount = total > 0 ? total : 0;
      this.summaryData = this.readSummary(grid);
      this.state = this.rowCount === 0 ? 'empty' : null;
    },
    readSummary(grid) {
      if (this.exportId === 'monthlyClosing') {
        return {
          sum: grid.getTotalSummaryValue('all_price'),
          sales: grid.getTotalSummaryValue('sale_count'),
          cancel: grid.getTotalSummaryValue('cancel_count'),
          pax: grid.getTotalSummaryValue('passenger_count')
        };
      }
      if (this.exportId === 'mvv') {
        return {
          count: grid.getTotalSummaryValue('Anzahl'),
          amount: grid.getTotalSummaryValue('Gesamtbetrag')
        };
      }
      return { rows: this.rowCount };
    },
    // Nullwerte #5b6472; Monatsabschluss: Tarifstufen unter der Ticket-Gruppe eingerückt
    onGridCellPrepared(e) {
      if (e.rowType !== 'data' || !e.cellElement) {
        return;
      }
      if (isZeroValue(e.value)) {
        e.cellElement.classList.add('app-null');
      }
      if (this.exportId === 'monthlyClosing' && e.column && e.column.dataField === 'tariff_name') {
        e.cellElement.classList.add('ex-einzug');
      }
    },
    updateDimensions() {
      if (this.grid) {
        this.grid.updateDimensions();
      }
    },
    selectExport(id) {
      if (!EXPORTS[id] || id === this.exportId) {
        return;
      }
      if (id === 'citybus' && (!this.fromMonth || this.fromMonth > this.month)) {
        this.fromMonth = this.month;
      }
      this.grid = null;
      this.exportId = id;
      this.search = '';
      this.expanded = false;
      this.state = 'loading';
      this.rowCount = 0;
      this.summaryData = null;
      writeStore(KEY_EXPORT, id);
    },
    onCitybusPeriod(period) {
      this.fromMonth = period.from;
      this.month = period.to;
    },
    setGrouping(value) {
      this.maGrouping = value;
      writeStore(KEY_GROUPING, value);
      const active = this.$refs.active;
      if (active && this.exportId === 'monthlyClosing') {
        active.groupSelection = value;
        active.onGroupingChange();
      }
    },
    toggleGroups() {
      const active = this.$refs.active;
      if (active && typeof active.collapseAllClick === 'function') {
        active.collapseAllClick();
      }
    },
    onSearch() {
      const active = this.$refs.active;
      if (!active) {
        return;
      }
      if (this.isCitybus) {
        if (this.grid) {
          this.grid.searchByText(this.search || '');
        }
        return;
      }
      active.searchText = this.search;
      active.onSearchInput();
    },
    refreshPreview() {
      const active = this.$refs.active;
      if (active) {
        active.refreshDataGrid();
      }
    },
    async runExport() {
      const active = this.$refs.active;
      if (!active || this.exportDisabled) {
        return;
      }
      if (!this.exportDef.csv) {
        active.exportToExcel();
        return;
      }
      // Die CSV-Methoden verändern die Grid-Zeilen: danach neu laden, sonst wäre ein zweiter Export falsch
      try {
        await active.exportToCsv();
      } catch (e) {
        // abgelehnt (z. B. weil das Grid gerade lädt): keine Datei; die Vorschau wird unten neu geladen
      } finally {
        active.refreshDataGrid();
      }
    },
    lineText(line) {
      return line.description ? line.line_id + ' ' + line.description : String(line.line_id);
    },
    onLinesOpen() {
      this.linesDraft = this.nvmSelected.slice();
    },
    toggleDraftAll() {
      // gleiche Regel wie NvmExportComponent.toggleAllLines, aber auf dem Entwurf (ein Ladevorgang beim Schließen)
      this.linesDraft = this.draftAll ? [] : this.nvmLines.map(line => line.line_id);
    },
    toggleDraftLine(id, checked) {
      const rest = this.linesDraft.filter(v => v !== id);
      this.linesDraft = checked ? rest.concat([id]) : rest;
    },
    onLinesClose() {
      const active = this.$refs.active;
      if (!active || this.exportId !== 'nvm') {
        return;
      }
      // Reihenfolge wie in der Linienliste; keine Auswahl bedeutet wie bisher „alle“ (kein Linienfilter)
      const draft = this.linesDraft;
      let next = this.nvmLines.map(line => line.line_id).filter(id => draft.indexOf(id) !== -1);
      if (!next.length) {
        next = this.nvmLines.map(line => line.line_id);
      }
      if (!sameSet(next, active.selectedLineIds)) {
        active.selectedLineIds = next;
      }
    }
  }
};
</script>

<style scoped>
.ex-preview {
  display: flex;
  flex-direction: column;
}
/* Handy: Komponente bleibt montiert (Export beruht auf dem Grid), Vorschau unsichtbar */
.ex-preview--hidden {
  flex: none;
  visibility: hidden;
  height: 0;
  overflow: hidden;
}
/* Summen stehen in der Fußleiste der Seite; die Summenzeile des Grids nur ausblenden (Werte bleiben für
   getTotalSummaryValue und die Excel-Datei) */
.ex-preview ::v-deep .dx-datagrid-total-footer {
  display: none;
}
/* Kopf, Gruppenzeilen und Gruppenfuß (0 12, außen 20) sowie der mittige Aufklapp-Chevron kommen aus devextreme.css */
/* Monatsabschluss: Tarifstufen unter der Ticket-Gruppe eingerückt */
.ex-preview ::v-deep .dx-datagrid .dx-datagrid-rowsview .dx-data-row > td.ex-einzug {
  padding-left: 28px;
}
/* Gruppensummen in Zahlenspalten rechtsbündig */
.ex-preview ::v-deep .dx-datagrid td.app-zahl .dx-datagrid-summary-item {
  text-align: right !important;
}
.ex-hint {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  white-space: nowrap;
}
.ex-hint--running {
  font-weight: 500;
  color: var(--amber);
}
.ex-hint--phone {
  flex: 1 1 100%;
  font-size: 13px;
  line-height: 18px;
  white-space: normal;
}
/* Werkzeugleiste: Gruppierung bzw. Linien rechtsbündig (wie bei Verkäufen; Kundenwunsch 13.10.2026) */
.ex-page:not(.app-page--phone) .ex-rechts {
  margin-left: auto;
}
.ex-full {
  flex: 1 1 100%;
}
.ex-zero {
  color: var(--ink-3);
}
.ex-searchnote {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.ex-lines {
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}
.ex-lines .ex-lines__row {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  min-height: var(--h-menu);
  padding: 0 12px;
}
.ex-phone {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px var(--page-x, 12px);
}
p.ex-phone__line {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
/* Summenzeile: Teile („davon 23 storniert“) nicht in der Mitte umbrechen */
p.ex-phone__line--sum > span {
  white-space: nowrap;
}
p.ex-phone__line strong,
.ex-phone__strong,
p.ex-phone__line--strong {
  font-weight: 600;
  color: var(--ink);
}
p.ex-phone__line strong.ex-zero {
  color: var(--ink-3);
}
</style>
